import { AlertTriangle, BellOff, XCircle, X } from "lucide-react";
import { Button, cn } from "@headerhawk/ui";
import type { UsageStatus } from "@/lib/api/client";
import { formatReportCount } from "@/lib/hooks/use-usage";

export interface QuotaBannerProps {
  status: UsageStatus;
  reportCount: number;
  softExceededCount: number;
  limit: number | null;
  onDismiss?: () => void;
  onUpgrade?: () => void;
}

interface BannerConfig {
  icon: React.ReactNode;
  title: string;
  message: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
  canDismiss: boolean;
}

function getBannerConfig(
  status: UsageStatus,
  percentUsed: number,
  softExceededCount: number,
): BannerConfig | null {
  switch (status) {
    case "under":
      return null;

    case "warning":
      return {
        icon: <AlertTriangle className="h-5 w-5" />,
        title: `You've used ${percentUsed}% of your monthly quota`,
        message: "Consider upgrading to avoid interruptions.",
        bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
        borderColor: "border-yellow-200 dark:border-yellow-800",
        textColor: "text-yellow-800 dark:text-yellow-200",
        iconColor: "text-yellow-500",
        canDismiss: true,
      };

    case "soft_exceeded":
      return {
        icon: <BellOff className="h-5 w-5" />,
        title: `Quota exceeded - ${formatReportCount(softExceededCount)} reports hidden`,
        message:
          "New reports are being stored but won't appear in your dashboard until you upgrade or your quota resets.",
        bgColor: "bg-orange-50 dark:bg-orange-900/20",
        borderColor: "border-orange-200 dark:border-orange-800",
        textColor: "text-orange-800 dark:text-orange-200",
        iconColor: "text-orange-500",
        canDismiss: true,
      };

    case "hard_exceeded":
      return {
        icon: <XCircle className="h-5 w-5" />,
        title: "Report collection paused",
        message:
          "Your quota is exhausted. New CSP violations are being rejected. Upgrade now to resume collection.",
        bgColor: "bg-red-50 dark:bg-red-900/20",
        borderColor: "border-red-200 dark:border-red-800",
        textColor: "text-red-800 dark:text-red-200",
        iconColor: "text-red-500",
        canDismiss: false,
      };
  }
}

export function QuotaBanner({
  status,
  reportCount,
  softExceededCount,
  limit,
  onDismiss,
  onUpgrade,
}: QuotaBannerProps) {
  // Floored like the API's `percentUsed` (task 278): rounding showed "80%"
  // while the status was still under the warning threshold.
  const percentUsed =
    limit !== null && limit > 0 ? Math.floor((reportCount * 100) / limit) : 0;

  const config = getBannerConfig(status, percentUsed, softExceededCount);

  if (!config) {
    return null;
  }

  return (
    <div
      className={cn(
        "rounded-lg border p-4",
        config.bgColor,
        config.borderColor,
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className={cn("mt-0.5 flex-shrink-0", config.iconColor)}>
          {config.icon}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className={cn("font-medium", config.textColor)}>
            {config.title}
          </h3>
          <p className={cn("mt-1 text-sm", config.textColor, "opacity-90")}>
            {config.message}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {onUpgrade && (
            <Button size="sm" onClick={onUpgrade}>
              Upgrade
            </Button>
          )}
          {config.canDismiss && onDismiss && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDismiss}
              className={cn(
                "h-8 w-8",
                config.textColor,
                "hover:bg-transparent",
              )}
              aria-label="Dismiss quota warning"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
